import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api, type Tool, type ChatThread } from '../shared/api-client';
import { useAuth } from '../auth/auth-hooks';
import { useFeatureFlags } from '../feature-flags/feature-flag-hooks';
import { userHasManagedRole } from '../permissions/managed-role-hooks';
import { CalloutBannerComponent } from '../shared/components/CalloutBannerComponent';
import { ChatSpineComposerComponent } from '../chat/ChatSpineComposerComponent';
import { TaskTemplatesBoard } from './DashboardBoardDestinations';
import { FilesBrowserPage } from '../files/FilesBrowserPage';
import './DashboardPage.css';

/** The four job groups the tool board is organised by (frame 3b). Replaces the
 * registry's General Tools / GTM / Knowledge / Project Management split — the
 * board is grouped by the job the user is doing, not by the registry's internal
 * taxonomy. AI Chat and Files leave the board entirely (chat lives in the
 * spine; files live in the board destinations). */
type JobGroupId = 'delivery-operations' | 'go-to-market';
const JOB_GROUP_ORDER: JobGroupId[] = ['go-to-market', 'delivery-operations'];
const JOB_GROUP_TITLES: Record<JobGroupId, string> = {
  'go-to-market': 'Go to market',
  'delivery-operations': 'Delivery & operations',
};

/** A unified tile — every board entry (registry tool or static link) is one. */
type BoardTile = {
  key: string;
  to: string;
  title: string;
  description: string;
  icon: JSX.Element;
  external?: boolean;
  color?: string;
  status?: string;
  statusNote?: string;
};

/** Shared SVG attributes for the static tiles' inline icons. */
const svgProps = {
  viewBox: '0 0 24 24',
  width: '22',
  height: '22',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.5',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const IconAccountDb = (
  <svg {...svgProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconIncidents = (
  <svg {...svgProps}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);
const IconPackageBox = (
  <svg {...svgProps}>
    <path d="M12 2.5 21 7v10l-9 4.5L3 17V7l9-4.5z" />
    <path d="M3 7l9 4.5L21 7" />
    <path d="M12 11.5v10" />
  </svg>
);
const IconLinkedIn = (
  <svg {...svgProps}>
    <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
    <path d="M14 3v5h5" />
    <path d="M9 12h6M9 16h6M9 8h2" />
  </svg>
);
const IconSiteEditor = (
  <svg {...svgProps}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18" />
    <path d="M8 14l2 2 4-4" />
  </svg>
);
const IconApiExplorer = (
  <svg {...svgProps}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
    <path d="M8 11h6M11 8v6" />
  </svg>
);
const IconSupportLab = (
  <svg {...svgProps}>
    <path d="M9 3h6M10 3v6.5L4.5 19a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 9.5V3" />
    <path d="M7.5 14h9" />
  </svg>
);
const IconProjects = (
  <svg {...svgProps}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M9 4v16" />
  </svg>
);
const IconMcp = (
  <svg {...svgProps}>
    <rect x="2" y="2" width="8" height="8" rx="2" />
    <rect x="14" y="14" width="8" height="8" rx="2" />
    <path d="M6 10v4a4 4 0 0 0 4 4h4" />
  </svg>
);

/** Map a registry tool identifier (or static-tile key) to its job group.
 * Falls back to 'delivery-operations' for unknowns — a safe, non-empty bucket. */
function jobGroupFor(identifier: string): JobGroupId {
  switch (identifier) {
    // Go-to-market: anything client-facing, sales, pricing, or account-related.
    case 'company-enrichment':
    case 'client-communications':
    case 'account-database':
    case 'sales-intelligence':
    case 'linkedin-post-generator':
    case 'pricing-calculator-v2':
    case 'competitive-pricing':
    case 'oms-roi-calculator':
    case 'rfp-qualifier-v2':
    case 'gtm-mission-control':
    case 'order-management-roi-calculator':
      return 'go-to-market';
    // Delivery & operations: internal surfaces plus the assistant-feeding tools
    // (MCP servers, projects) — no separate "feeding" group.
    case 'grafana-triage':
    case 'api-explorer':
    case 'incidents':
    case 'ticket-deployment-visibility':
    case 'support-lab':
    case 'projects':
    case 'mcp-servers':
      return 'delivery-operations';
    default:
      return 'delivery-operations';
  }
}

/** Spine tab ids. Files/Tasks board destinations come in the next commit. */

export function Dashboard(): JSX.Element {
  const [tools, setTools] = useState<Tool[]>([]);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const { user } = useAuth();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const boardParam = searchParams.get('board');
  const activeBoard: 'files' | 'tasks' | null =
    boardParam === 'files' ? 'files' : boardParam === 'tasks' ? 'tasks' : null;
  // Which threads the spine lists lives in the URL (?threads=), per the deep-linking rule.
  const threadsParam = searchParams.get('threads');
  const threadView: 'my' | 'group' | 'all' =
    threadsParam === 'group' ? 'group' : threadsParam === 'all' ? 'all' : 'my';
  const setThreadView = (view: 'my' | 'group' | 'all'): void => {
    const next = new URLSearchParams(searchParams);
    if (view === 'my') next.delete('threads');
    else next.set('threads', view);
    setSearchParams(next, { replace: true });
  };
  const closeBoard = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('board');
    setSearchParams(next, { replace: true });
  };
  // On the stacked (<=900px) layout the spine renders above the board, so a
  // full thread list pushes every app tile below the fold. Collapsed is the
  // default; ?chats=expanded reopens it (deep-linking rule).
  const chatsExpanded = searchParams.get('chats') === 'expanded';
  const toggleChats = (): void => {
    const next = new URLSearchParams(searchParams);
    if (chatsExpanded) next.delete('chats');
    else next.set('chats', 'expanded');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    document.title = 'Dashboard - Kibo Hub';

    async function loadTools(): Promise<void> {
      try {
        const result = await api.getTools();
        if (result.success) {
          setTools(result.data);
        } else {
          setError(result.error.message);
        }
      } catch {
        setError('Failed to load tools');
      } finally {
        setLoading(false);
      }
    }

    void loadTools();
  }, []);

  // Load the thread list for the spine. Same view/limit shape as ChatPage's
  // sidebar; reloaded when the view changes.
  useEffect(() => {
    void api.getChatThreads({ view: threadView, limit: 50 }).then((result) => {
      if (result.success) setThreads(result.data.items);
    });
  }, [threadView]);

  if (loading || flagsLoading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="loading">Loading tools...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  // FILES_DASHBOARD_TILE is UI-only: it takes the File Library off the dashboard
  // (spine tab, board destination and ?board=files) while leaving /apps/files
  // reachable by direct link for people already using it.
  const showFiles = isEnabled('FILES_DASHBOARD_TILE');

  const activeTools = tools.filter((t) => t.metadata?.isActive !== false);

  let visibleTools = activeTools;
  visibleTools = visibleTools.filter((t) => t.identifier !== 'mintlify-agent');
  visibleTools = visibleTools.filter((t) => t.identifier !== 'salesforce-cases');
  // AI Chat leaves the board — it is the spine composer now.
  visibleTools = visibleTools.filter((t) => t.identifier !== 'chat');
  // The Grafana triage app/route is gated by the GRAFANA_TRIAGE flag (see App.tsx's
  // FeatureFlagRoute); keep the tile in lockstep so a flag-off env doesn't show a
  // tile that dead-ends on NotFound.
  if (!isEnabled('GRAFANA_TRIAGE')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'grafana-triage');
  }
  // The Company Enrichment tile is gated by the company-enrichment managed role,
  // mirroring App.tsx's ManagedRoleAccessRoute so the tile never dead-ends on Forbidden.
  if (!userHasManagedRole(user, 'company-enrichment')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'company-enrichment');
  }
  // The Sales Intelligence tile is gated by the SALES_INTELLIGENCE flag AND the
  // sales-intelligence managed role: the flag is the global on/off, and the role
  // check mirrors App.tsx's ManagedRoleAccessRoute so the tile never dead-ends on
  // Forbidden. Within the role, viewer tier sees their own profile and manager
  // tier sees the team-wide view; the tile is shown to any tier.
  if (!isEnabled('SALES_INTELLIGENCE') || !userHasManagedRole(user, 'sales-intelligence')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'sales-intelligence');
  }
  // The Implementation Portal tile is gated by the CLIENT_HUB flag, mirroring App.tsx's
  // FeatureFlagRoute so the tile never dead-ends on a 404. Phase 1 is
  // capture only — flag on is the whole gate, there is no per-user role yet.
  if (!isEnabled('CLIENT_HUB')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'client-hub');
  }
  // The LinkedIn Post Generator tile is gated by the LINKEDIN_POST_GENERATOR
  // flag AND the linkedin-post-generator managed role: the flag is the global
  // on/off, and the role check mirrors App.tsx's ManagedRoleAccessRoute so the
  // tile never dead-ends on Forbidden.
  if (!isEnabled('LINKEDIN_POST_GENERATOR') || !userHasManagedRole(user, 'linkedin-post-generator')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'linkedin-post-generator');
  }
  // The Client Communications tile is gated by the CLIENT_COMMUNICATIONS flag.
  // The role half is already enforced server-side — GET /api/v1/tools filters
  // every tile by a tool:<identifier> viewer check — so only the flag is left
  // to mirror App.tsx's FeatureFlagRoute here.
  if (!isEnabled('CLIENT_COMMUNICATIONS')) {
    visibleTools = visibleTools.filter((t) => t.identifier !== 'client-communications');
  }

  // Build the board tiles, regrouped by job. Registry tools first, then the
  // static inserts appended into their job group with the same guards as
  // before the redesign.
  const grouped = new Map<JobGroupId, BoardTile[]>();
  for (const g of JOB_GROUP_ORDER) grouped.set(g, []);
  for (const tool of visibleTools) {
    const g = jobGroupFor(tool.identifier);
    grouped.get(g)!.push(toolToTile(tool));
  }

  // --- Static tiles (guards mirror the old render exactly) ---
  grouped.get('go-to-market')!.push({
    key: 'static-accounts',
    to: '/apps/accounts',
    title: 'Account Database',
    description: 'Unified client account database with tenants, partner access, and delivery pods',
    icon: IconAccountDb,
    color: 'kiwi',
  });
  // Sits in delivery & operations: an incident is an operational surface first,
  // and the AI investigation is one thing it can start rather than the point.
  grouped.get('delivery-operations')!.push({
    key: 'static-incidents',
    to: '/apps/incidents',
    title: 'Incidents',
    description: 'Declare a Sev1 or Sev2: opens a transcribing bridge, posts the war room, drafts the RCA',
    icon: IconIncidents,
    color: 'strawberry',
  });
  // Gated by the DEPLOYMENT_VISIBILITY flag AND the deployment-visibility managed
  // role, mirroring App.tsx so the tile never dead-ends on NotFound or Forbidden.
  if (isEnabled('DEPLOYMENT_VISIBILITY') && userHasManagedRole(user, 'deployment-visibility')) {
    grouped.get('delivery-operations')!.push({
      key: 'static-deployment-visibility',
      to: '/apps/deployment-visibility',
      title: 'Ticket Deployment Visibility',
      description: 'What shipped, what rolled back, and what is live where — per Sandbox and per TP, straight from Jenkins',
      icon: IconPackageBox,
      color: 'orange',
    });
  }
  // Also delivery & operations: the lab is a testing surface for support, not
  // an AI entry point — the AI is the thing under test rather than the thing
  // offered.
  grouped.get('delivery-operations')!.push({
    key: 'static-support-lab',
    to: '/apps/support-lab',
    title: 'Support Lab',
    description: 'Open a ticket as if you were a client and watch the real support AI handle it',
    icon: IconSupportLab,
    color: 'lime',
  });
  // Gated by the API_EXPLORER flag AND the api-explorer managed role: the flag
  // is the global on/off, and the role check mirrors App.tsx's
  // ManagedRoleAccessRoute so the tile never dead-ends on Forbidden.
  if (isEnabled('API_EXPLORER') && userHasManagedRole(user, 'api-explorer')) {
    grouped.get('delivery-operations')!.push({
      key: 'static-api-explorer',
      to: '/apps/api-explorer',
      title: 'API Explorer',
      description: 'Search every Kibo endpoint, browse a tenant’s objects, and change one field without rebuilding the whole payload',
      icon: IconApiExplorer,
      color: 'blueberry',
    });
  }
  if (isEnabled('LINKEDIN_POST_GENERATOR') && userHasManagedRole(user, 'linkedin-post-generator')) {
    grouped.get('go-to-market')!.push({
      key: 'static-linkedin',
      to: '/apps/linkedin-post-generator',
      title: 'LinkedIn Post Generator',
      description: 'Turn a Kibo asset into a ready-to-post LinkedIn caption — three distinct tones, written to sound human',
      icon: IconLinkedIn,
      color: 'blueberry',
    });
  }
  if (isEnabled('SITE_EDITOR') && userHasManagedRole(user, 'site-editor')) {
    grouped.get('go-to-market')!.push({
      key: 'static-site-editor',
      to: '/apps/site-editor',
      title: 'Site Editor',
      description: 'Change kibocommerce.com by asking for it — see it live, send it to staging, publish when it’s ready',
      icon: IconSiteEditor,
      color: 'blueberry',
    });
  }
  // Files leaves the board — it is a spine tab and a board destination, both
  // gated on FILES_DASHBOARD_TILE (see showFiles). Projects and MCP Servers sit
  // with delivery & operations. There is deliberately no AI Keys tile: the AI
  // gateway is always on because workspace chat runs its model calls through it,
  // but the self-serve key page is not offered — /apps/ai-keys still resolves for
  // anyone with the link.
  if (isEnabled('CHAT_PROJECTS')) {
    grouped.get('delivery-operations')!.push({
      key: 'static-projects',
      to: '/apps/projects',
      title: 'Projects',
      description: 'Shared folders the assistant can read in any chat',
      icon: IconProjects,
      color: 'grape',
    });
  }
  if (isEnabled('MCP_SERVERS')) {
    grouped.get('delivery-operations')!.push({
      key: 'static-mcp-servers',
      to: '/apps/mcp-servers',
      title: 'MCP Servers',
      description: 'Use your Hub AI tools from Claude Code, Cursor, or any MCP client',
      icon: IconMcp,
      color: 'navy',
    });
  }

  // Alphabetical order within every group — the board reads as a scannable
  // list of tool names, not as a record of the order they were pushed in.
  for (const g of JOB_GROUP_ORDER) {
    grouped.get(g)!.sort((a, b) => a.title.localeCompare(b.title));
  }

  // Drop groups that ended up empty.
  const visibleGroups = JOB_GROUP_ORDER.filter((g) => grouped.get(g)!.length > 0);

  const normalizedQuery = query.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  // Flat search across all groups. The board is tens of tiles, so this is cheap
  // enough to compute every render — skipping a memo avoids a dependency on the
  // per-render rebuilt `grouped` Map and the lint suppression that would need.
  const flatResults = isSearching
    ? visibleGroups.flatMap((g) =>
        grouped.get(g)!.map((t) => ({ ...t, groupTitle: JOB_GROUP_TITLES[g] })),
      ).filter((t) => {
        const haystack = `${t.title} ${t.description} ${t.groupTitle}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      }).sort((a, b) => a.title.localeCompare(b.title))
    : [];

  return (
    <div className="dashboard">
      <div className="dash-shell">
        {/* ===== Spine (left) — frame 3b ===== */}
        <aside className="dash-spine">
          <div className="dash-spine-head">
            <span className="dash-spine-brand" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M8 9h8" />
                <path d="M8 13h6" />
              </svg>
            </span>
            <span className="dash-spine-title">AI Chat</span>
            <button
              className="dash-spine-new"
              title="New chat"
              aria-label="New chat"
              onClick={async () => {
                const result = await api.getCurrentUser();
                if (!result.success) { window.location.reload(); return; }
                navigate('/apps/chat');
              }}
            >
              +
            </button>
          </div>

          <ChatSpineComposerComponent />

          <div className="dash-spine-pills">
            <Link to="/apps/chat/scheduled" className="dash-spine-pill">Schedule a prompt</Link>
            <Link to="/apps/chat/settings/tasks" className="dash-spine-pill">
              My tasks
            </Link>
          </div>

          <div className="dash-spine-tabs" role="tablist" aria-label="Threads">
            {([['my', 'My Threads'], ['group', 'Group Threads'], ['all', 'All Threads']] as const).map(([v, label]) => (
              <button
                key={v}
                role="tab"
                aria-selected={threadView === v}
                className={`dash-spine-tab${threadView === v ? ' active' : ''}`}
                onClick={() => setThreadView(v)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`dash-spine-threads${chatsExpanded ? '' : ' dash-spine-threads--collapsed'}`}>
              <button
                type="button"
                className="dash-spine-threads-toggle"
                aria-expanded={chatsExpanded}
                aria-controls="dash-spine-thread-list-scroll"
                onClick={toggleChats}
              >
                <span>Past chats</span>
                <span className="dash-spine-threads-toggle-count" aria-hidden="true">{threads.length}</span>
                <span className="dash-spine-threads-toggle-chevron" aria-hidden="true">›</span>
              </button>
              <div className="dash-spine-thread-list-scroll" id="dash-spine-thread-list-scroll">
                <div className="dash-spine-thread-list">
                  {threads.map((t) => (
                    <Link
                      key={t.id}
                      to={`/apps/chat/${t.id}`}
                      className="dash-spine-thread"
                    >
                      <div className="dash-spine-thread-title">
                        {t.isPrivate && <span className="dash-spine-thread-private" title="Private" aria-label="Private chat">🔒</span>}
                        <span className="dash-spine-thread-title-text">{t.title || 'Untitled'}</span>
                        {t.isPinned && (
                          <span className="dash-spine-thread-pin" title="Pinned — you pinned this chat" aria-label="Pinned chat">
                            <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" stroke="none">
                              <path d="M16 3l5 5-1.4 1.4-.6-.6-3.2 3.2.3 3.3-1.4 1.4-3.1-3.1L6 20l-2-2 6.4-6.6L7.3 8.3l1.4-1.4 3.3.3 3.2-3.2-.6-.6L15 2.9 16 3z" />
                            </svg>
                          </span>
                        )}
                      </div>
                      <div className="dash-spine-thread-meta">
                        {threadView !== 'my' && <span>{t.user.displayName}</span>}
                        <span>{t.messageCount} msgs</span>
                      </div>
                    </Link>
                  ))}
                  {threads.length === 0 && (
                    <div className="dash-spine-thread-empty">No threads yet</div>
                  )}
                </div>
              </div>
              <Link to="/apps/chat/search" className="dash-spine-all-threads">Search all threads →</Link>
          </div>

          {user?.buildInfo && (
            <div className="dash-spine-version">
              v {user.buildInfo.shortSha} · {user.buildInfo.deployTag}
            </div>
          )}
        </aside>

        {/* ===== Board (right) — frame 3b/4a ===== */}
        <section className="dash-board">
          {activeBoard === 'files' && showFiles ? (
            <div className="dash-board-files-host">
              <button type="button" className="dash-board-back" onClick={closeBoard} aria-label="Back to dashboard">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>
              <div className="dash-board-files-frame">
                <FilesBrowserPage />
              </div>
            </div>
          ) : activeBoard === 'tasks' ? (
            <TaskTemplatesBoard onClose={closeBoard} />
          ) : (
            <>
          <div className="dash-board-head">
            <div className="dash-board-greeting">
              <h1 className="dash-board-hello">Kibo Hub Dashboard</h1>
            </div>
            <div className="dash-board-search">
              <span className="dash-board-search-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                className="dash-board-search-input"
                placeholder="Search tools…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search tools"
              />
              {query && (
                <button className="dash-board-search-clear" onClick={() => setQuery('')} aria-label="Clear search">✕</button>
              )}
            </div>
          </div>

          <CalloutBannerComponent page="dashboard" />

          <div className="dash-board-destinations">
            {showFiles && (
              <Link to="?board=files" className="dash-board-destination">
                <span className="dash-board-destination-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                </span>
                <span className="dash-board-destination-body">
                  <span className="dash-board-destination-title">File Library</span>
                  <span className="dash-board-destination-desc">Documents the assistant can read in any chat</span>
                </span>
                <span className="dash-board-destination-chevron" aria-hidden="true">›</span>
              </Link>
            )}
            <Link to="?board=tasks" className="dash-board-destination">
              <span className="dash-board-destination-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </span>
              <span className="dash-board-destination-body">
                <span className="dash-board-destination-title">Task Templates</span>
                <span className="dash-board-destination-desc">Structured prompts with their own fields and tool sets</span>
              </span>
              <span className="dash-board-destination-chevron" aria-hidden="true">›</span>
            </Link>
          </div>

          {visibleGroups.length === 0 && !isSearching ? (
            <div className="no-tools">
              <p>No tools available.</p>
              <p className="text-secondary">
                You may not have permission to access any tools, or no tools have been configured.
              </p>
            </div>
          ) : isSearching ? (
            flatResults.length > 0 ? (
              <div className="dash-board-group-card">
                <div className="dash-board-group-eyebrow">Results · {flatResults.length}</div>
                {flatResults.map((tile) => (
                  <TileRow key={tile.key} tile={tile} />
                ))}
              </div>
            ) : (
              <p className="dash-search-empty">No tools match “{query}”.</p>
            )
          ) : (
            <div className="dash-board-groups">
              {visibleGroups.map((g) => (
                <div key={g} className="dash-board-group-card">
                  <div className="dash-board-group-eyebrow">{JOB_GROUP_TITLES[g]}</div>
                  {grouped.get(g)!.map((tile) => (
                    <TileRow key={tile.key} tile={tile} />
                  ))}
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function toolToTile(tool: Tool): BoardTile {
  return {
    key: tool.id,
    to: tool.metadata.url,
    title: tool.name,
    description: tool.description ?? '',
    icon: <ToolIcon icon={tool.metadata.icon} />,
    external: isExternalHref(tool),
    color: tool.metadata.color,
    status: tool.metadata.status,
    statusNote: tool.metadata.statusNote,
  };
}

function ToolIcon({ icon }: { icon?: string }): JSX.Element {
  switch (icon) {
    case 'document-question':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <circle cx="12" cy="14" r="3" />
          <path d="M12 17v.5" />
          <path d="M10.5 12.5a1.5 1.5 0 1 1 2.1 1.4c-.4.2-.6.5-.6.9" />
        </svg>
      );
    case 'calculator':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="8" y1="10" x2="8" y2="10.01" />
          <line x1="12" y1="10" x2="12" y2="10.01" />
          <line x1="16" y1="10" x2="16" y2="10.01" />
          <line x1="8" y1="14" x2="8" y2="14.01" />
          <line x1="12" y1="14" x2="12" y2="14.01" />
          <line x1="16" y1="14" x2="16" y2="14.01" />
          <line x1="8" y1="18" x2="8" y2="18.01" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
          <line x1="16" y1="18" x2="16" y2="18.01" />
        </svg>
      );
    case 'mintlify-agent':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      );
    case 'chat':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M8 9h8" />
          <path d="M8 13h6" />
        </svg>
      );
    case 'documentation':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      );
    case 'alert-triage':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'company-enrichment':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <line x1="9" y1="10" x2="9" y2="10.01" />
          <line x1="15" y1="10" x2="15" y2="10.01" />
          <line x1="9" y1="14" x2="9" y2="14.01" />
          <line x1="15" y1="14" x2="15" y2="14.01" />
        </svg>
      );
    case 'client-communications':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case 'sales-intelligence':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18" />
          <path d="M7 14l3-3 3 3 5-6" />
          <circle cx="18" cy="8" r="1.2" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18" />
          <path d="M9 21V9" />
        </svg>
      );
  }
}

/** Every tool is either a Hub page or a link out; there is no third rendering. */
function isExternalHref(tool: Tool): boolean {
  return tool.metadata.linkType !== 'internal';
}

/** A tool row inside a job-group card (frame 3b/4a/5a). The whole row is the
 *  link; the icon is a small tinted square; there is no top accent bar and no
 *  status badge — frame 3b shows neither. */
function TileRow({ tile }: { tile: BoardTile }): JSX.Element {
  const navigate = useNavigate();
  const external = tile.external;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (external) return;
    e.preventDefault();
    navigate(tile.to);
  };

  return (
    <a
      className={`tool-row${tile.color ? ` tool-row--${tile.color}` : ''}`}
      href={tile.to}
      onClick={handleClick}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <span className="tool-row-icon" aria-hidden="true">{tile.icon}</span>
      <span className="tool-row-body">
        <span className="tool-row-title">{tile.title}</span>
        {tile.description && <span className="tool-row-desc">{tile.description}</span>}
      </span>
    </a>
  );
}

